import { MaxDropLimits } from "helpers/types";
import { useStrategyReportValue } from "./useReportStorage";

const MAX_DROP_LIMITS_KEY = "max_drop_limits_strategy";
const DEFAULT_MAX_DROP = 20;

// Mirrors the stock report's rule, clamped to the magnitude the edit modal
// accepts so a derived limit is always one the user could have typed. Each
// drop is null when the row carries no figure, and a null takes no part in
// the derivation rather than standing in it as a 0.
//
// With NEITHER drop a figure -- an unrun report, or two rows the backend
// refused -- there is nothing to derive from, and the answer is null: "No
// Report", the same state the two bars show. A ±20% stood here and was shown
// in the edit modal and sent with the PDF as though it were this asset's
// limit, a scale no run measured (task 4264). Reading an absent figure as 0
// instead produced a 1% axis, the same claim at a different size. Nothing is
// drawn against the limits without a figure, so null costs the chart nothing.
const deriveLimits = (
  stockDrop: number | null,
  oeaDrop: number | null,
): MaxDropLimits | null => {
  const drops = [stockDrop, oeaDrop].filter(
    (drop): drop is number => drop !== null,
  );
  if (drops.length === 0) return null;

  if (drops.every((drop) => drop < 0)) {
    const deepest = Math.floor(Math.min(...drops));
    return { maxDrop: DEFAULT_MAX_DROP, negativeLimit: Math.min(deepest, -1) };
  }

  const widest = Math.ceil(Math.max(...drops));
  return { maxDrop: Math.max(widest, 1), negativeLimit: -DEFAULT_MAX_DROP };
};

/**
 * `symbol` is "" until a report has been calculated, and until then there are
 * no limits at all: the chart says "No Report" and cannot be edited (task
 * 4264). A limit typed before the run used to be saved under "" and was never
 * read again once the run picked a real asset, so editing then only looked
 * like it did something -- and a stale "" entry left behind by that is
 * ignored here rather than shown as a limit.
 *
 * After a run the limits are still null when this asset has neither a saved
 * override nor a drop to derive them from (see deriveLimits).
 */
export const useMaxDropLimits = (
  symbol: string,
  stockDrop: number | null,
  oeaDrop: number | null,
): [MaxDropLimits | null, (limits: MaxDropLimits) => void] => {
  const [storedValue, setStoredValue] = useStrategyReportValue(
    MAX_DROP_LIMITS_KEY,
    "{}",
  );
  const saved: Record<string, MaxDropLimits> = JSON.parse(String(storedValue));

  // Both limits are written together — two setters would each save the entry
  // from their own closure and the second would drop the first.
  const setLimits = (limits: MaxDropLimits) => {
    if (!symbol) return;
    setStoredValue(JSON.stringify({ ...saved, [symbol]: limits }));
  };

  if (!symbol) return [null, setLimits];
  return [saved[symbol] ?? deriveLimits(stockDrop, oeaDrop), setLimits];
};
